import React from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../../i18n/resolve-key';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { FilterList as FilterListIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import type { TableColumnDescriptor } from '@finance/contracts';
import type { FilterOperatorDescriptor } from '../filters/filter-registry.js';

interface FilterOperatorMenuProps {
  column: TableColumnDescriptor;
  currentOperator: string;
  onChange: (operator: string) => void;
  allowedOperators: FilterOperatorDescriptor[];
  disabled?: boolean;
}

export function FilterOperatorMenu({ column, currentOperator, onChange, allowedOperators, disabled }: FilterOperatorMenuProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (operator: string) => {
    onChange(operator);
    handleClose();
  };

  const validOperators = allowedOperators.filter((op) =>
    op.applicableTypes.includes(column.fieldType ?? 'text')
  );

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={disabled}
        aria-label={t(resolveTKey('filters.operator'))}
        aria-expanded={!!anchorEl}
        aria-haspopup="true"
      >
        <FilterListIcon fontSize="small" color={currentOperator !== 'contains' && currentOperator !== 'equals' ? 'primary' : 'action'} />
        <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {validOperators.map((op) => (
          <MenuItem key={op.id} onClick={() => handleSelect(op.id)} selected={op.id === currentOperator}>
            {t(resolveTKey(op.labelKey))}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function createFilterOperatorMenu() {
  return React.memo(FilterOperatorMenu);
}